import { useState, useEffect } from 'react';
import { 
  Download, 
  Save, 
  Trash2, 
  Info,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';
import type { SoundEffect, ExportConfig, SavedExportConfig } from '../types';
import { exportApi } from '../lib/api';
import { supabase } from '../lib/supabase';
import { Button } from '@/components/ui/button';

interface ExportDialogProps {
  selectedSounds: SoundEffect[];
  onClose: () => void;
}

export function ExportDialog({ selectedSounds, onClose }: ExportDialogProps) {
  const [configs, setConfigs] = useState<SavedExportConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [customConfig, setCustomConfig] = useState<ExportConfig>({
    format: 'wav',
    sampleRate: 44100,
    bitDepth: 16,
    channels: 'stereo',
    normalization: 'none',
    trimSilence: false,
    namingPattern: '{name}',
    sanitizeFilenames: true,
    replaceSpaces: '_',
    caseConversion: 'none',
    indexPadding: 3,
    folderStructure: 'flat',
    includeMetadata: false,
    metadataFormat: 'json',
    metadataFields: ['duration', 'category']
  });
  const [isExporting, setIsExporting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saveConfigName, setSaveConfigName] = useState('');
  const [namingVariables, setNamingVariables] = useState<string[]>([]);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await exportApi.listConfigs(true);
      setConfigs(response.configs);
      setNamingVariables(response.namingVariables || []);
      
      // Select first config by default
      if (response.configs.length > 0) {
        setSelectedConfigId(response.configs[0].id);
        if (!response.configs[0].isDefault) {
          setCustomConfig(response.configs[0].config);
        }
      }
    } catch (error) {
      console.error('Failed to load configs:', error);
    }
  };

  const handleConfigChange = (configId: string) => {
    setSelectedConfigId(configId);
    const config = configs.find(c => c.id === configId);
    if (config && !config.isDefault) {
      setCustomConfig(config.config);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      console.log('Starting export...');
      const selectedConfig = configs.find(c => c.id === selectedConfigId);
      console.log('Selected config:', selectedConfig);
      
      const exportRequest = {
        soundIds: selectedSounds.map(s => s.id),
        configId: selectedConfig?.isDefault ? selectedConfigId : undefined,
        customConfig: !selectedConfig?.isDefault ? customConfig : undefined
      };
      console.log('Export request:', exportRequest);
      
      const response = await exportApi.createExport(exportRequest);
      console.log('Export API response:', response);
      
      // Download with authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No authentication token available");
      }

      console.log('Downloading from:', response.downloadUrl);
      const downloadResponse = await fetch(response.downloadUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      console.log('Download response:', downloadResponse.status, downloadResponse.statusText);
      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text();
        console.error('Download error response:', errorText);
        throw new Error(`Download failed: ${downloadResponse.status} ${downloadResponse.statusText}`);
      }

      // Create blob and trigger download
      const blob = await downloadResponse.blob();
      console.log('Blob size:', blob.size);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bardus-export-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
      // Close dialog after successful download
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!saveConfigName.trim()) {
      alert('Please enter a name for the configuration');
      return;
    }

    try {
      await exportApi.saveConfig({
        name: saveConfigName,
        config: customConfig,
        isPublic: false
      });

      await loadConfigs();
      setSaveConfigName('');
    } catch (error) {
      console.error('Failed to save config:', error);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!confirm('Delete this configuration?')) return;

    try {
      await exportApi.deleteConfig(configId);
      await loadConfigs();
    } catch (error) {
      console.error('Failed to delete config:', error);
    }
  };

  const estimatedSize = selectedSounds.reduce((acc, s) => acc + (s.size || 1000000), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl border border-border max-w-2xl w-full my-8">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold">Export {selectedSounds.length} Sound{selectedSounds.length !== 1 ? 's' : ''}</h2>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X size={20} />
          </Button>
        </div>
        
        <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Configuration Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Configuration</label>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedConfigId}
                onChange={(e) => handleConfigChange(e.target.value)}
                className="flex-1 px-3 py-2 bg-secondary rounded-lg border border-border"
              >
                {configs.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.name} {config.isDefault ? '(Default)' : ''}
                  </option>
                ))}
              </select>
              {selectedConfigId && !configs.find(c => c.id === selectedConfigId)?.isDefault && (
                <Button
                  onClick={() => handleDeleteConfig(selectedConfigId)}
                  variant="ghost"
                  size="icon"
                  className="hover:text-destructive"
                  title="Delete configuration"
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          </div>

          {/* Custom Configuration */}
          {selectedConfigId && !configs.find(c => c.id === selectedConfigId)?.isDefault && (
            <div className="space-y-4">
              {/* Audio Format */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Format</label>
                  <select 
                    value={customConfig.format}
                    onChange={(e) => setCustomConfig({...customConfig, format: e.target.value as any})}
                    className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                  >
                    <option value="wav">WAV (Uncompressed)</option>
                    <option value="mp3">MP3 (Compressed)</option>
                    <option value="ogg">OGG Vorbis</option>
                    <option value="flac">FLAC (Lossless)</option>
                    <option value="original">Keep Original</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Sample Rate</label>
                  <select 
                    value={customConfig.sampleRate}
                    onChange={(e) => setCustomConfig({...customConfig, sampleRate: e.target.value === 'original' ? 'original' : Number(e.target.value) as any})}
                    className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                  >
                    <option value="22050">22.05 kHz</option>
                    <option value="44100">44.1 kHz</option>
                    <option value="48000">48 kHz</option>
                    <option value="96000">96 kHz</option>
                    <option value="original">Keep Original</option>
                  </select>
                </div>
              </div>

              {/* File Naming */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  File Naming Pattern
                  <button
                    type="button"
                    className="ml-2 text-muted-foreground hover:text-foreground"
                    title="Available variables"
                  >
                    <Info size={14} />
                  </button>
                </label>
                <input
                  type="text"
                  value={customConfig.namingPattern}
                  onChange={(e) => setCustomConfig({...customConfig, namingPattern: e.target.value})}
                  className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                  placeholder="{category}_{name}_{index}"
                />
                <div className="mt-1 text-xs text-muted-foreground">
                  Available: {namingVariables.map(v => `{${v}}`).join(', ')}
                </div>
              </div>

              {/* Folder Structure */}
              <div>
                <label className="block text-sm font-medium mb-2">Folder Structure</label>
                <select 
                  value={customConfig.folderStructure}
                  onChange={(e) => setCustomConfig({...customConfig, folderStructure: e.target.value as any})}
                  className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                >
                  <option value="flat">Flat (No folders)</option>
                  <option value="category">By Category</option>
                  <option value="tags">By Tags</option>
                  <option value="custom">Custom Pattern</option>
                </select>
              </div>

              {/* Advanced Options */}
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-sm font-medium p-0 h-auto hover:bg-transparent"
                >
                  {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span>Advanced Options</span>
                </Button>

                {showAdvanced && (
                  <div className="mt-4 space-y-4 pl-6">
                    {/* Audio Processing */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">Audio Processing</h4>
                      
                      <div>
                        <label className="block text-sm mb-2">Normalization</label>
                        <select 
                          value={customConfig.normalization}
                          onChange={(e) => setCustomConfig({...customConfig, normalization: e.target.value as any})}
                          className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                        >
                          <option value="none">None</option>
                          <option value="peak">Peak Normalization</option>
                          <option value="lufs">LUFS (Loudness)</option>
                        </select>
                      </div>

                      <label className="flex items-center space-x-2">
                        <input 
                          type="checkbox"
                          checked={customConfig.trimSilence}
                          onChange={(e) => setCustomConfig({...customConfig, trimSilence: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Trim silence from start/end</span>
                      </label>
                    </div>

                    {/* File Naming Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">File Naming</h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm mb-2">Replace Spaces</label>
                          <select 
                            value={customConfig.replaceSpaces}
                            onChange={(e) => setCustomConfig({...customConfig, replaceSpaces: e.target.value as any})}
                            className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                          >
                            <option value="_">Underscore (_)</option>
                            <option value="-">Hyphen (-)</option>
                            <option value="remove">Remove</option>
                            <option value="keep">Keep Spaces</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm mb-2">Case Conversion</label>
                          <select 
                            value={customConfig.caseConversion}
                            onChange={(e) => setCustomConfig({...customConfig, caseConversion: e.target.value as any})}
                            className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                          >
                            <option value="none">No Change</option>
                            <option value="lower">lowercase</option>
                            <option value="upper">UPPERCASE</option>
                            <option value="camel">camelCase</option>
                            <option value="snake">snake_case</option>
                          </select>
                        </div>
                      </div>

                      <label className="flex items-center space-x-2">
                        <input 
                          type="checkbox"
                          checked={customConfig.sanitizeFilenames}
                          onChange={(e) => setCustomConfig({...customConfig, sanitizeFilenames: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">Sanitize filenames (remove special characters)</span>
                      </label>
                    </div>

                    {/* Metadata */}
                    <div className="space-y-3">
                      <label className="flex items-center space-x-2">
                        <input 
                          type="checkbox"
                          checked={customConfig.includeMetadata}
                          onChange={(e) => setCustomConfig({...customConfig, includeMetadata: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">Include metadata file</span>
                      </label>

                      {customConfig.includeMetadata && (
                        <>
                          <div>
                            <label className="block text-sm mb-2">Metadata Format</label>
                            <select 
                              value={customConfig.metadataFormat}
                              onChange={(e) => setCustomConfig({...customConfig, metadataFormat: e.target.value as any})}
                              className="w-full px-3 py-2 bg-secondary rounded-lg border border-border"
                            >
                              <option value="json">JSON</option>
                              <option value="xml">XML</option>
                              <option value="csv">CSV</option>
                              <option value="txt">Plain Text</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm mb-2">Include Fields</label>
                            <div className="space-y-1">
                              {['duration', 'category', 'tags', 'prompt', 'createdAt'].map(field => (
                                <label key={field} className="flex items-center space-x-2">
                                  <input 
                                    type="checkbox"
                                    checked={customConfig.metadataFields?.includes(field)}
                                    onChange={(e) => {
                                      const fields = customConfig.metadataFields || [];
                                      if (e.target.checked) {
                                        setCustomConfig({...customConfig, metadataFields: [...fields, field]});
                                      } else {
                                        setCustomConfig({...customConfig, metadataFields: fields.filter(f => f !== field)});
                                      }
                                    }}
                                    className="rounded"
                                  />
                                  <span className="text-sm capitalize">{field}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Save Configuration */}
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={saveConfigName}
                  onChange={(e) => setSaveConfigName(e.target.value)}
                  placeholder="Save configuration as..."
                  className="flex-1 px-3 py-2 bg-secondary rounded-lg border border-border"
                />
                <Button
                  onClick={handleSaveConfig}
                  disabled={!saveConfigName.trim()}
                  variant="secondary"
                >
                  <Save size={16} />
                  <span>Save</span>
                </Button>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="text-sm font-medium mb-2">Export Summary</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• {selectedSounds.length} sound{selectedSounds.length !== 1 ? 's' : ''} selected</li>
              <li>• Format: {customConfig.format === 'original' ? 'Keep original' : customConfig.format.toUpperCase()}</li>
              <li>• Estimated size: ~{Math.round(estimatedSize / 1024 / 1024)}MB</li>
              {customConfig.includeMetadata && <li>• Includes {customConfig.metadataFormat.toUpperCase()} metadata file</li>}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-border flex justify-between">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            variant="default"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download size={16} />
                <span>Export</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}